import { EditRightAccess, ObjectRightType, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import { Item } from "@dto/records";
import { TW_DecimalCell, TW_DecimalCell_editing } from "@utils/twClasses";
import { displayDecimal, valueStringOrDefault } from "@utils/types";
import { useEffect, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import { AbstractCell } from "./AbstractCell";
import { CellProps } from "./GridCell";


export function DecimalCell({ pathKey, field, item, position, updateValue, isEditing, inputRef, blurTriggered }: CellProps) {
    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;
    
    const decRegex = /^-?\d*\.?\d*$/;

    const [text, setText] = useState(valueStringOrDefault(item?.value, ""));
    const wsColor = useModelStore(state => state.wsColor);
    const [lastBlurTriggered, setLastBlurTriggered] = useState<number | null>(null);
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    
    useEffect(() => {
        if (item?.value !== text) {
            setText(valueStringOrDefault(item?.value, ""));
        }
    }, [item]);

    useEffect(() => {
        const newValue = textToValue(text);
        if (isEditing && lastBlurTriggered && blurTriggered && lastBlurTriggered !== blurTriggered && item?.value !== newValue) {
            updateValue(item, newValue);
        }
        setLastBlurTriggered(blurTriggered);
    }, [blurTriggered]);

    const updateLocalValue = (item: Item, value: any) => {
        setText(value || "");
        updateValue(item, value);
    }

    // console.log("Rendering DecimalCell at " + posToString(position) + " with item " + item?.value);

    const textToValue = (text: string | null): any => {
        if (!text || text.length === 0) {
            return null;
        }
        if (text === "-") {
            return 0;
        }
        if (text === "Error") {
            return "Error";
        }
        return parseFloat(text);
    }

    const valueToText = (): string => {
        return displayDecimal(textToValue(text), format);
    }

    function isTextValid(value: string | null) {
        if (!value || value.length === 0) {
            return true;
        }
        return decRegex.test(value);
    }

    const renderReadOnlyContent = () => {
        const valueStr = valueToText();
        return (
            <div className={TW_DecimalCell + (readOnly ? " cursor-not-allowed" : "")}>
                {valueStr}
            </div>
        )
    }

    const renderEditingContent = (
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
        onEnterKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void,
        inputRef: React.RefObject<HTMLInputElement>
    ) => {
        return (
            <>
            <input
                className={TW_DecimalCell_editing}
                type="text"
                value={text}
                onChange={onTextChange}
                onKeyDown={onEnterKeyPress}
                autoFocus={true}
                style={{ borderColor: wsColor }}
                ref={inputRef}
            />
        </>)
    }

    return <AbstractCell
        item={item}
        position={position}
        updateValue={updateLocalValue}
        renderReadOnlyContent={renderReadOnlyContent}
        renderEditingContent={renderEditingContent}
        textToValue={textToValue}
        isTextValid={isTextValid}
        text={text}
        setText={setText}
        isEditing={isEditing}
        inputRef={inputRef}
        readOnly={readOnly}
    />
}
